import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { MesCosechaArray } from "../../../Arrays/MesCosechaArray";
import { construirColorPorAnio } from "../../../Helpers/ColoresPorAnio";

// Gráfica de "Venta mensual" (línea suavizada). Valor por defecto de
// `datos` si nadie lo manda.
const datosMock = [
    37268, 67649, 102919, 131492, 292490, 354824, 160218, 298104, 362104, 324176, 210300, 280400,
];

// `series` (`[{ anio, valores: [12] }]`, opcional): una línea por año,
// coloreada con `construirColorPorAnio`, tiene prioridad sobre `datos`
// (arreglo plano de 12 valores, un solo año con relleno verde). `metrica`
// solo cambia el formato del tooltip ($ vs. unidades).
export const ChartVentaMensual = ({
    id = "chart-venta-mensual",
    datos = datosMock,
    series,
    metrica = "monto",
}) => {
    const chartRef = useRef(null);

    const usaMultiSerie = Array.isArray(series) && series.length > 0;
    const colorPorAnio = usaMultiSerie ? construirColorPorAnio(series.map((s) => s.anio)) : {};

    useEffect(() => {
        const dom = chartRef.current;
        if (!dom) return;

        if (echarts.getInstanceByDom(dom)) {
            echarts.dispose(dom);
        }

        const myChart = echarts.init(dom);
        const formatMoneda =
            metrica === "cantidad"
                ? (value) => `${Math.round(value).toLocaleString("es-MX")} unidades`
                : (value) => `$${FormatMoney(value, 0)}`;

        const seriesChart = usaMultiSerie
            ? series.map((s) => ({
                name: String(s.anio),
                type: "line",
                data: s.valores,
                smooth: true,
                symbolSize: 6,
                lineStyle: { color: colorPorAnio[s.anio], width: 2 },
                itemStyle: { color: colorPorAnio[s.anio] },
            }))
            : [
                {
                    type: "line",
                    data: datos,
                    smooth: true,
                    symbolSize: 6,
                    lineStyle: { color: "#367c2b", width: 2 },
                    itemStyle: { color: "#255220" },
                    areaStyle: { color: "rgba(74, 158, 60, 0.15)" },
                },
            ];

        const option = {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "line" },
                formatter: (params) =>
                    usaMultiSerie
                        ? `<strong>${params[0].name}</strong><br/>${params
                            .map((p) => `${p.marker} ${p.seriesName}: ${formatMoneda(p.value)}`)
                            .join("<br/>")}`
                        : `${params[0].name}<br/>${formatMoneda(params[0].value)}`,
            },
            legend: usaMultiSerie
                ? { bottom: 0, itemWidth: 12, itemHeight: 12, textStyle: { fontSize: 11, color: "#333" } }
                : undefined,
            grid: {
                left: "8%",
                right: "4%",
                top: "10%",
                bottom: usaMultiSerie ? "10%" : "10%",
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: MesCosechaArray.map((m) => m.display.slice(0, 3)),
                axisLine: { lineStyle: { color: "#ccc" } },
                axisLabel: { color: "#333", fontSize: 10 },
            },
            yAxis: { type: "value", show: false },
            series: seriesChart,
        };

        myChart.setOption(option);

        const handleResize = () => myChart.resize();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            myChart.dispose();
        };
    }, [datos, series, usaMultiSerie, colorPorAnio, metrica]);

    return <div id={id} ref={chartRef} style={{ width: "100%", height: "100%" }} />;
};
